import AppHeader from '@/components/AppHeader';
import FilterSidebar from '@/components/FilterSidebar';
import Header from '@/components/Header';
import NewsContent from '@/components/NewsContent';
import NewsWorkspacePanel from '@/components/NewsWorkspacePanel';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { useAllNews, useFilteredNews } from '@/hooks/useNews';
import { Filter } from '@/lib/types';
import { extractProviders, extractRegions } from '@/lib/utils';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const Terminal: React.FC = () => {
  const [filter, setFilter] = useState<Filter>({});
  const [isSidebarVisible, setSidebarVisible] = useState(true);
  const { data: allNews, isLoading } = useAllNews(1000);
  const { data: filteredNews, refetch } = useFilteredNews(filter, 1000);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [theme, setTheme] = useState<'amber' | 'green' | 'white'>('amber');
  const [mode, setMode] = useState<'live' | 'search' | 'popular'>('live');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const totalPages = filteredNews ? Math.max(1, Math.ceil(filteredNews.length / pageSize)) : 1;

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Extract providers and regions from the news data
  const providers = allNews ? extractProviders(allNews) : [];
  const regions = allNews ? extractRegions(allNews) : [];

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // F10 - Advanced Settings
      if (e.key === 'F10') {
        e.preventDefault();
        console.log('F10 pressed');

        // If sidebar is hidden, show it
        if (!isSidebarVisible) {
          setSidebarVisible(true);
        }

        // Cycle themes as a temporary "Advanced" action
        const themes: ('amber' | 'green' | 'white')[] = ['amber', 'green', 'white'];
        const nextTheme = themes[(themes.indexOf(theme) + 1) % themes.length];
        setTheme(nextTheme);

        toast({
          title: "Advanced Settings",
          description: `Theme changed to ${nextTheme.toUpperCase()}. F10 cycles themes.`,
          duration: 2000,
        });
      }

      // ESC - Clear filters
      if (e.key === 'Escape' && Object.keys(filter).length > 0) {
        e.preventDefault();
        setFilter({});
        toast({
          title: "Filters Reset",
          description: "All filters have been cleared.",
          duration: 2000,
        });
      }

      // PageUp - Previous page
      if (e.key === 'PageUp') {
        e.preventDefault();
        setCurrentPage(prev => Math.max(1, prev - 1));
      }

      // PageDown - Next page
      if (e.key === 'PageDown') {
        e.preventDefault();
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSidebarVisible, theme, filter, totalPages]);

  // Debug: Log providers to console for troubleshooting
  useEffect(() => {
    if (providers.length > 0) {
      console.log('Providers found:', providers);
    }
  }, [providers]);

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
  };

  // Handle filter changes
  const handleFilterChange = (newFilter: Filter) => {
    // Ensure we're creating a new object for state updates
    if (Object.keys(newFilter).length === 0) {
      // If filters are being reset, ensure we create a fresh empty object
      setFilter({});
    } else {
      setFilter({ ...newFilter });
    }

    // Show toast notification
    toast({
      title: Object.keys(newFilter).length ? "Filter Applied" : "Filters Reset",
      description: `${Object.keys(newFilter).length ? 'Filtered by ' + Object.values(newFilter).join(', ') : 'All filters cleared'}`,
      duration: 2000,
    });
  };

  // Adjust sidebar visibility for mobile on initial load
  useEffect(() => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  }, [isMobile]);

  // Handle manual refresh
  const handleRefresh = () => {
    refetch();
    toast({
      title: "Refreshing News",
      description: "Fetching the latest news updates...",
      duration: 3000,
    });
  };

  return (
    <>
      <Helmet>
        <title>News Terminal — Live Market & World Headlines | Tylee Smart Markets</title>
        <meta name="description" content="Full-screen financial news terminal. Filter 1,000+ live headlines by source, region, or category. Updated in real time from WSJ, Reuters, Bloomberg, FT, CNBC, and more." />
        <link rel="canonical" href="https://tyl.ee/news" />
        <meta property="og:title" content="Live News Terminal — Tylee Smart Markets" />
        <meta property="og:description" content="Filter 1,000+ headlines by source or region. Live from WSJ, Reuters, Bloomberg, FT, CNBC, and 30+ sources." />
        <meta property="og:url" content="https://tyl.ee/news" />
        <meta name="twitter:title" content="Live News Terminal — Tylee Smart Markets" />
        <meta name="twitter:description" content="Filter 1,000+ headlines by source or region. Live from WSJ, Reuters, Bloomberg, FT, CNBC, and 30+ sources." />
      </Helmet>
      <div className={`flex flex-col h-screen bg-black theme-${theme}`}>
        {/* Skip link: visually hidden until focused, allows keyboard users to bypass header/sidebar */}
        <a href="#main-content" className="skip-link">Skip to main content</a>

        <AppHeader brandText="News" />

      <Header
        onToggleSidebar={toggleSidebar}
        isSidebarVisible={isSidebarVisible}
        currentPage={currentPage}
        totalPages={totalPages}
        mode={mode}
        onModeChange={setMode}
      />

      <div className="flex flex-1 overflow-hidden relative">
        <aside aria-label="News filters">
          <FilterSidebar
            isVisible={isSidebarVisible}
            providers={providers}
            regions={regions}
            activeFilter={filter}
            onFilterChange={handleFilterChange}
            onClose={toggleSidebar}
            className="sidebar-container"
          />
        </aside>

        <main
          id="main-content"
          className={`flex-1 transition-all duration-200 ${isMobile && isSidebarVisible ? 'opacity-30' : 'opacity-100'}`}
        >
          {/* aria-live region announces news count changes to screen readers */}
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="sr-only"
          >
            {filteredNews ? `${filteredNews.length} news items` : ''}
          </div>
          {mode === 'live' ? (
            <div className="flex h-full min-h-0 flex-col">
              <NewsWorkspacePanel mode="live" />
              <NewsContent filter={filter} currentPage={currentPage} pageSize={pageSize} />
            </div>
          ) : (
            <NewsWorkspacePanel mode={mode} />
          )}
        </main>
      </div>
      </div>
    </>
  );
};

export default Terminal;
